import React from 'react';
import {
  View,
  Modal,
  StyleSheet,
  TouchableOpacity,
  Text,
  Alert
} from 'react-native';
import { IconButton, useTheme, TextInput } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';

interface HandwritingInputProps {
  visible: boolean;
  onClose: () => void;
  onTextConverted: (text: string) => void;
  initialText?: string;
  placeholder?: string;
}

const HandwritingInput: React.FC<HandwritingInputProps> = ({
  visible,
  onClose,
  onTextConverted,
  initialText = '',
  placeholder = 'Write here...'
}) => {
  const theme = useTheme();
  const [currentText, setCurrentText] = React.useState(initialText);

  React.useEffect(() => {
    setCurrentText(initialText);
  }, [initialText]);

  const handleClear = () => {
    setCurrentText('');
  };

  const handleSubmit = () => {
    if (!currentText || currentText.trim() === '') {
      Alert.alert('No Content', 'Please write something before submitting.');
      return;
    }
    
    onTextConverted(currentText);
    handleClear();
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalContainer}>
        <View style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.header}>
            <Text style={[styles.title, { color: theme.colors.onSurface }]}>
              Text Input (Web Mode)
            </Text>
            <IconButton
              icon="close"
              size={24}
              onPress={onClose}
              style={styles.closeButton}
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              label={placeholder}
              value={currentText}
              onChangeText={setCurrentText}
              multiline
              numberOfLines={8}
              mode="outlined"
              style={styles.textInput}
            />
            <Text style={[styles.note, { color: theme.colors.outline }]}>
              Note: Handwriting recognition is not available on web. Use the text input above.
            </Text>
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.clearButton, { borderColor: theme.colors.error }]}
              onPress={handleClear}
            >
              <MaterialCommunityIcons name="eraser" size={20} color={theme.colors.error} />
              <Text style={[styles.buttonText, { color: theme.colors.error }]}>Clear</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, styles.submitButton, { backgroundColor: theme.colors.primary }]}
              onPress={handleSubmit}
            >
              <MaterialCommunityIcons name="check" size={20} color={theme.colors.onPrimary} />
              <Text style={[styles.buttonText, { color: theme.colors.onPrimary }]}>Submit</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    width: '90%',
    maxWidth: 600,
    height: '70%',
    borderRadius: 8,
    padding: 16,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  closeButton: {
    margin: -8,
  },
  inputContainer: {
    flex: 1,
    marginBottom: 16,
  },
  textInput: {
    flex: 1,
    textAlignVertical: 'top',
  },
  note: {
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 8,
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  clearButton: {
    borderWidth: 1,
  },
  submitButton: {
    elevation: 2,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default HandwritingInput;